import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Check, Gift, Loader2, LogIn, ShieldCheck, Sparkles, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { track } from "@/lib/meta";
import { oppref, trackConversionPixel } from "@/lib/oaiq";

const STEPS = [
  "Copy your AppSumo redemption code",
  "Create your Propojo account or log in",
  "Paste the code below and hit Redeem",
  "Your Lifetime Deal activates instantly",
];

const TIERS = [
  { codes: 1, price: 69, proposals: 100 },
  { codes: 2, price: 138, proposals: 300 },
  { codes: 3, price: 207, proposals: 500 },
];

const INCLUDED = [
  "AI proposal generation (wizard + transcript)",
  "All 15 editor blocks & drag-and-drop",
  "Unlimited brand kits & templates",
  "10 team seats",
  "Client CRM with website auto-fill",
  "Multi-currency + GST engine",
  "Secure share links with password & expiry",
  "Open, time-spent & section tracking",
  "Online acceptance with e-signature",
  "PDF & Word export",
  "Analytics dashboard with funnel & CSV",
];

export default function LifetimeDeal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null); // {ok, message}
  const [status, setStatus] = useState(null);

  useEffect(() => {
    track("ViewContent", { content_name: "lifetime-deal-redemption", content_category: "appsumo" });
    if (user) api.get("/appsumo/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const redeem = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setState(null);
    try {
      const { data } = await api.post("/appsumo/redeem", { code, oppref: oppref() });
      setState({ ok: true, message: data.message });
      // ChatGPT Ads: pixel copy of the server's confirmed lifetime subscription event.
      trackConversionPixel(
        "subscription_created",
        { type: "plan_enrollment" },
        data.oai_event_id,
      );
      setStatus((p) => ({ ...p, ...data, lifetime: true }));
      setCode("");
      if (!data.next_tier) setTimeout(() => navigate("/dashboard"), 2200);
    } catch (err) {
      setState({ ok: false, message: apiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen nv-grid-bg text-zinc-100">
      <header className="mx-auto max-w-5xl px-5 py-5 flex items-center justify-between">
        <Logo size={26} withWordmark />
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-num uppercase tracking-[0.18em] text-amber-300">
          <Gift className="w-3 h-3" /> AppSumo lifetime deal
        </span>
      </header>

      <main className="mx-auto max-w-5xl px-5 pb-20 pt-6 grid lg:grid-cols-[1fr_0.9fr] gap-6 items-start">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold tracking-tight leading-[1.18]"
          >
            Redeem your Propojo
            <span className="block text-indigo-400">Lifetime Deal</span>
          </motion.h1>
          <p className="mt-4 max-w-lg text-sm md:text-base text-zinc-400">
            Paste the code from your AppSumo purchase and your workspace is upgraded for life — every feature, no
            monthly bill, ever.
          </p>

          <ol className="mt-7 space-y-2.5">
            {STEPS.map((s, i) => (
              <motion.li
                key={s}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 * i }}
                className="flex items-start gap-3 text-sm text-zinc-300"
              >
                <span className="grid place-items-center h-5 w-5 shrink-0 rounded-md bg-indigo-500/15 text-[10px] font-num text-indigo-300">
                  {i + 1}
                </span>
                {s}
              </motion.li>
            ))}
          </ol>

          <div className="mt-7 grid sm:grid-cols-3 gap-2.5">
            {TIERS.map((t) => (
              <div
                key={t.codes}
                data-testid={`ltd-tier-${t.codes}`}
                className="rounded-2xl border border-zinc-800 bg-zinc-900/30 p-4 hover:border-indigo-500/40 transition-colors"
              >
                <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">
                  {t.codes} {t.codes === 1 ? "code" : "codes"}
                </p>
                <p className="mt-1.5 text-2xl font-bold font-num tracking-tight">${t.price}</p>
                <p className="mt-1 text-xs text-indigo-300">{t.proposals} proposals / month</p>
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-2xl border border-zinc-800 bg-zinc-900/30 p-5">
            <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500">
              Every tier includes · stack codes to raise your monthly limit
            </p>
            <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {INCLUDED.map((t) => (
                <span key={t} className="flex items-start gap-2 text-xs text-zinc-300">
                  <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* redemption card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          data-testid="redeem-card"
          className="nv-card p-5 lg:sticky lg:top-6"
        >
          {!user ? (
            <>
              <ShieldCheck className="w-7 h-7 text-indigo-300" />
              <p className="mt-3 text-lg font-semibold tracking-tight">First, your Propojo account</p>
              <p className="mt-1.5 text-xs text-zinc-400">
                Create a free account (or sign in) and you'll come straight back here to redeem.
              </p>
              <button
                data-testid="ltd-signup-btn"
                onClick={() => {
                  track("StartTrial", { currency: "INR", value: 0, content_name: "appsumo_ltd" });
                  navigate("/signup?next=/lifetime-deal");
                }}
                className="mt-5 w-full h-11 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              >
                Create my account <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="ltd-login-btn"
                onClick={() => navigate("/login?next=/lifetime-deal")}
                className="mt-2 w-full h-11 rounded-xl border border-zinc-800 hover:border-zinc-700 text-sm text-zinc-300 inline-flex items-center justify-center gap-2 transition-colors"
              >
                <LogIn className="w-4 h-4" /> I already have an account
              </button>
            </>
          ) : status?.codes_redeemed >= status?.max_codes ? (
            <>
              <BadgeCheck className="w-8 h-8 text-emerald-400" />
              <p className="mt-3 text-lg font-semibold tracking-tight">Lifetime Deal already active</p>
              <p className="mt-1.5 text-xs text-zinc-400">
                All {status.max_codes} codes stacked · {status.monthly_proposals} proposals every month.
              </p>
              <button
                data-testid="ltd-dashboard-btn"
                onClick={() => navigate("/dashboard")}
                className="mt-5 w-full h-11 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              >
                Go to dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <form onSubmit={redeem}>
              <Sparkles className="w-7 h-7 text-indigo-300" />
              <p className="mt-3 text-lg font-semibold tracking-tight">Enter your AppSumo code</p>
              <p className="mt-1.5 text-xs text-zinc-400">Signed in as {user.email}</p>
              {status?.codes_redeemed > 0 && (
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] text-emerald-200">
                  <BadgeCheck className="w-3 h-3" />
                  {status.codes_redeemed} of {status.max_codes} codes stacked · {status.monthly_proposals}/month
                  {status.next_tier ? ` · stack one more for ${status.next_tier}` : ""}
                </p>
              )}
              <input
                data-testid="ltd-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="PJP-XXXX-XXXX-XXXX"
                autoComplete="off"
                spellCheck={false}
                className="mt-4 w-full h-12 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 font-num tracking-[0.12em] text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500/60 focus:outline-none transition-colors"
              />
              <button
                data-testid="ltd-redeem-btn"
                type="submit"
                disabled={busy || code.trim().length < 3}
                className="mt-3 w-full h-12 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {busy ? "Redeeming…" : "Redeem"}
              </button>

              {state && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={state.ok ? "ltd-success" : "ltd-error"}
                  className={`mt-4 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-xs ${
                    state.ok
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                      : "border-red-500/40 bg-red-500/10 text-red-200"
                  }`}
                >
                  {state.ok ? (
                    <BadgeCheck className="w-4 h-4 shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 shrink-0" />
                  )}
                  <span>
                    {state.message}
                    {state.ok && <span className="block mt-0.5 opacity-80">Taking you to your dashboard…</span>}
                  </span>
                </motion.div>
              )}

              <p className="mt-4 text-[11px] leading-relaxed text-zinc-500">
                Each code works once. Trouble redeeming? Email support@propojo.com with your AppSumo order number.
              </p>
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
}
